'use client'

import { useAuthorization } from '@entities/authentication'
import { isDevicePermissionSyncCapableUserAgent, updateDeviceTokenFromBrowser } from '@features/native'
import { loggingAction } from '@shared/apis'
import { NativeBridgeStoreContext, OSType } from '@shared/models'
import { SPA_NAV_COUNT_KEY } from '@shared/ui/top-navigation'
import { createLinkBridgeProvider } from '@webview-bridge/react'
import { linkBridge } from '@webview-bridge/web'
import { usePathname, useRouter } from 'next/navigation'
import { ComponentType, ReactNode, useCallback, useEffect, useRef } from 'react'

import { type AppBridge, type AppPostMessageSchema } from '@podo/native'

import { deviceSyncCoordinator } from './device-sync-coordinator'
import { nativeLineLoginBridge } from './native-line-login.bridge'

type DeviceSyncOS = 'ios' | 'android'

const normalizeDeviceSyncOS = (os: OSType): DeviceSyncOS | undefined => {
  if (os === 'IOS' || os === 'IPAD') {
    return 'ios'
  }

  if (os === 'ANDROID' || os === 'ANDROID_TABLET') {
    return 'android'
  }

  return undefined
}

type Props = {
  children: ReactNode
  os: OSType
  from: 'APP' | 'WEB'
  runtimeVersion: Nullable<string>
  latestRuntimeVersion: Nullable<string>
  userAgent: Nullable<string>
}

export const linkBridgeProvider = createLinkBridgeProvider<AppBridge, AppPostMessageSchema>({
  throwOnError: true,
  initialBridge: {
    appLaunchId: '',
    pushOptInPromptState: null,
    pushToken: null,
    notificationPermissionStatus: null,
    permissionSyncRevision: 0,
  },
})

const { BridgeProvider, useBridgeStore, useBridgeStatus, useBridgeLoose, useBridgeEventListener } = linkBridgeProvider

const withBridgetStore =
  <T extends object>(Component: ComponentType<T>) =>
  (props: T) => {
    return (
      <BridgeProvider>
        <Component {...props} />
      </BridgeProvider>
    )
  }
export const webBridge = linkBridge<AppBridge>({
  throwOnError: true,
})

export const NativeBridgeStoreProvider = withBridgetStore(
  ({ children, os, from, runtimeVersion, latestRuntimeVersion, userAgent }: Props) => {
    const bridgeState = useBridgeStore((state) => state)
    const authorization = useAuthorization()
    const bearerToken = authorization?.isAuthorized ? authorization.bearerToken : undefined
    const userId = authorization?.isAuthorized ? authorization.uid : undefined

    const pathname = usePathname()
    const router = useRouter()

    // useBridgeEventListener 는 마운트 시 1회만 리스너를 등록하므로(내부 useEffect deps []),
    // 콜백이 항상 최신 router 를 참조하도록 ref 로 보관한다.
    const routerRef = useRef(router)
    const synchronizedDeviceSyncRef = useRef<string | null>(null)
    const deviceSyncAttemptRef = useRef<string | null>(null)
    const deviceSyncQueueRef = useRef<Promise<void>>(Promise.resolve())
    const latestPushTokenRef = useRef<string | null>(bridgeState.pushToken)
    const inFlightPushTokenRef = useRef<Promise<string | null> | null>(null)
    useEffect(() => {
      routerRef.current = router
    }, [router])
    useEffect(() => {
      if (bridgeState.pushToken !== null) {
        latestPushTokenRef.current = bridgeState.pushToken
      }
    }, [bridgeState.pushToken])

    const getPushToken = useCallback((): Promise<string | null> => {
      if (latestPushTokenRef.current !== null) {
        return Promise.resolve(latestPushTokenRef.current)
      }

      if (inFlightPushTokenRef.current !== null) {
        return inFlightPushTokenRef.current
      }

      let timeoutId: ReturnType<typeof setTimeout> | undefined
      const timeout = new Promise<null>((resolve) => {
        timeoutId = setTimeout(() => resolve(null), 5000)
      })
      const request = Promise.race([webBridge.requestPushToken(), timeout]).then((token) => {
        const resolvedToken = latestPushTokenRef.current ?? token ?? null
        latestPushTokenRef.current = resolvedToken
        return resolvedToken
      })
      const sharedRequest = request.finally(() => {
        clearTimeout(timeoutId)
        inFlightPushTokenRef.current = null
      })
      inFlightPushTokenRef.current = sharedRequest

      return sharedRequest
    }, [])

    // 안드로이드 하드웨어 뒤로가기: native 가 보낸 눌림 신호를 받아 웹 라우터 상태로 판단한다.
    // - 홈(/home, /home/ai)에서는 항상 native 종료 UX(더블탭)로 위임한다.
    //   SPA 이동 카운트는 router.back() 이후에도 pathname 변경으로 증가하므로,
    //   "홈에서는 종료" 판정을 카운트보다 우선해 홈에서 종료가 막히지 않게 한다.
    // - 홈이 아니면 같은 탭에서 이동 이력이 있을 때(count > 1) 이전 페이지로 이동하고,
    //   이력이 없으면(콜드 스타트/딥링크/(external) 일회성 플로우) 홈으로 보낸다.
    //   ((external) 레이아웃에는 SpaHistoryTracker 가 없어 count 가 0 이므로 홈으로 이동한다)
    // 판단 값(카운트/현재 경로)은 콜백 실행 시점에 직접 읽어 stale closure 를 피한다.
    useBridgeEventListener('androidHardwareBackPress', () => {
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''
      if (currentPath === '/home' || currentPath === '/home/ai') {
        webBridge.requestExitApp().catch(() => {})
        return
      }

      const count = typeof sessionStorage !== 'undefined' ? Number(sessionStorage.getItem(SPA_NAV_COUNT_KEY) ?? '0') : 0
      if (count > 1) {
        routerRef.current.back()
      } else {
        routerRef.current.replace('/home')
      }
    })

    useEffect(() => {
      if (from !== 'APP') {
        return
      }
      // AI Learning 페이지에서는 SafeArea를 동적으로 제어하므로 강제 설정하지 않음
      if (pathname?.startsWith('/ai-learning')) {
        return
      }

      webBridge.setUseSafeArea(true)
    }, [from, pathname, bridgeState])

    // 앱 로드 완료 신호: gateway(/)가 아닌 실제 목적지 페이지가 렌더될 때 스플래시 숨김
    useEffect(() => {
      if (from !== 'APP' || pathname === '/') {
        return
      }

      webBridge.setAppLoaded(true)
    }, [from, pathname])

    useEffect(() => {
      if (from !== 'APP' || !bearerToken) {
        return
      }

      const normalizedOS = normalizeDeviceSyncOS(os)
      const permissionStatus = bridgeState.notificationPermissionStatus
      const supportsDevicePermissionSync =
        normalizedOS !== undefined && isDevicePermissionSyncCapableUserAgent(userAgent)

      // capability가 있는 native만 권한 상태 기반 payload를 이해한다. 해당
      // 런타임은 최초 권한 상태가 bridge에 반영될 때까지 기다린다.
      if (supportsDevicePermissionSync && permissionStatus === null) {
        return
      }

      let cancelled = false
      const controller = new AbortController()

      const synchronizeDevice = async () => {
        let deviceToken = bridgeState.pushToken ?? latestPushTokenRef.current

        if (deviceToken === null && (!supportsDevicePermissionSync || permissionStatus === 'granted')) {
          try {
            deviceToken = await getPushToken()
          } catch {
            // 권한은 token 없이도 동기화할 수 있다.
            deviceToken = null
          }
        }

        if (cancelled) {
          return
        }

        // 성공한 동기화는 경로가 바뀌어도 같은 revision/payload로 반복하지
        // 않는다. 실패한 요청만 pathname 변경 시 재시도할 수 있도록 별도의
        // attempt key를 사용한다.
        const syncKey = [
          userId,
          bridgeState.permissionSyncRevision,
          normalizedOS ?? os,
          permissionStatus ?? '',
          deviceToken ?? '',
        ].join(':')
        if (synchronizedDeviceSyncRef.current === syncKey) {
          return
        }

        const attemptKey = `${syncKey}:${pathname ?? ''}`
        if (deviceSyncAttemptRef.current === attemptKey) {
          return
        }
        deviceSyncAttemptRef.current = attemptKey

        let synchronized = false
        if (supportsDevicePermissionSync && normalizedOS && permissionStatus !== null) {
          let deviceIdentifier: string | null = null
          try {
            deviceIdentifier = await webBridge.requestDeviceIdentifier()
          } catch {
            // IDFV/Android ID를 읽지 못한 경우 native action이 legacy token
            // fallback을 선택할 수 있도록 null을 그대로 전달한다.
            deviceIdentifier = null
          }

          if (cancelled) {
            return
          }

          synchronized = await deviceSyncCoordinator.synchronize(
            // One webBridge/document belongs to one installation. Keep this
            // slot stable while its initial empty appLaunchId is populated.
            JSON.stringify(['native-installation', normalizedOS]),
            JSON.stringify([syncKey, deviceIdentifier]),
            controller.signal,
            () =>
              updateDeviceTokenFromBrowser(
                {
                  deviceToken,
                  deviceIdentifier,
                  os: normalizedOS,
                  permissionStatus,
                },
                bearerToken,
                controller.signal,
              ),
          )
        } else {
          // 구형 iOS/Android와 macOS/desktop은 새 식별자 metadata 없이
          // 기존 token-only 계약을 유지한다.
          synchronized = await deviceSyncCoordinator.synchronize(
            JSON.stringify(['native-installation', normalizedOS ?? os]),
            syncKey,
            controller.signal,
            () => updateDeviceTokenFromBrowser(deviceToken, bearerToken, controller.signal),
          )
        }

        if (!cancelled && synchronized) {
          synchronizedDeviceSyncRef.current = syncKey
        }
      }

      // 이전 권한 관측 요청이 늦게 완료되어 최신 상태를 덮지 않도록
      // 같은 웹뷰의 동기화를 순서대로 실행한다.
      deviceSyncQueueRef.current = deviceSyncQueueRef.current.catch(() => undefined).then(synchronizeDevice)

      return () => {
        cancelled = true
        controller.abort()
        deviceSyncAttemptRef.current = null
      }
    }, [
      from,
      pathname,
      bearerToken,
      userId,
      os,
      userAgent,
      getPushToken,
      bridgeState.permissionSyncRevision,
      bridgeState.appLaunchId,
      bridgeState.notificationPermissionStatus,
      bridgeState.pushToken,
    ])

    const loggingNativeInformation = useCallback(
      (error?: Error) => {
        // pushToken 은 임의 푸시 발송이 가능한 기기 자격증명이라 서버 로그로 내보내지 않는다.
        // 브릿지 디버깅에는 발급 여부만 있으면 충분하다.
        const { pushToken, ...bridgeStateWithoutCredentials } = bridgeState
        const serializedError = error
          ? {
              name: error.name,
              message: error.message,
              stack: error.stack,
            }
          : undefined

        loggingAction({
          nativeStore: JSON.stringify({
            error: serializedError,
            os,
            from,
            runtimeVersion,
            latestRuntimeVersion,
            userAgent,
            ...bridgeStateWithoutCredentials,
            hasPushToken: Boolean(pushToken),
          }),
        })
      },
      [bridgeState, os, from, runtimeVersion, latestRuntimeVersion, userAgent],
    )

    const withSafeLogging = async <T,>(callback: () => Promise<T>): Promise<T> => {
      try {
        return await callback()
      } catch (error) {
        loggingNativeInformation(error as Error)

        return Promise.resolve(null as unknown as T)
      }
    }

    return (
      <NativeBridgeStoreContext.Provider
        value={{
          from,
          os,
          runtimeVersion,
          latestRuntimeVersion,
          userAgent,

          // bridge state
          appLaunchId: bridgeState.appLaunchId,
          pushOptInPromptState: bridgeState.pushOptInPromptState,
          appLoaded: bridgeState.appLoaded,
          appLoadedAt: bridgeState.appLoadedAt,
          notificationPermissionStatus: bridgeState.notificationPermissionStatus,
          permissionSyncRevision: bridgeState.permissionSyncRevision,
          pushToken: bridgeState.pushToken,
          splashVisible: bridgeState.splashVisible,
          appStartedAt: bridgeState.appStartedAt,
          safeAreaBackgroundColor: bridgeState.safeAreaBackgroundColor,
          useSafeArea: bridgeState.useSafeArea,

          // bridge action
          setAppLoaded: async (isLoaded) => {
            return withSafeLogging(async () => await webBridge.setAppLoaded(isLoaded))
          },
          setNotificationPermissionStatus: (notificationPermissionStatus) => {
            return withSafeLogging(
              async () => await webBridge.setNotificationPermissionStatus(notificationPermissionStatus),
            )
          },
          requestDeviceIdentifier: () => {
            return withSafeLogging(async () => await webBridge.requestDeviceIdentifier())
          },
          alertNotificationPermission: () => {
            return withSafeLogging(async () => await webBridge.alertNotificationPermission())
          },
          linkingSettings: () => {
            return withSafeLogging(async () => await webBridge.linkingSettings())
          },
          requestNotificationPermission: () => {
            return withSafeLogging(async () => await webBridge.requestNotificationPermission())
          },
          refreshNotificationPermission: () => {
            return withSafeLogging(async () => await webBridge.refreshNotificationPermission())
          },
          markStep1DeclinedForCurrentLaunch: () => {
            return withSafeLogging(async () => await webBridge.markStep1DeclinedForCurrentLaunch())
          },
          consumePushOptInStep1ForCurrentLaunch: (webviewUrl: string) => {
            const bridgeWithLocalStep1 = webBridge as typeof webBridge & {
              consumePushOptInStep1ForCurrentLaunch: (url: string) => Promise<void>
            }
            return withSafeLogging(
              async () => await bridgeWithLocalStep1.consumePushOptInStep1ForCurrentLaunch(webviewUrl),
            )
          },
          markStep2Shown: () => {
            return withSafeLogging(async () => await webBridge.markStep2Shown())
          },
          checkAndRequestNotificationPermission: () => {
            return withSafeLogging(async () => await webBridge.checkAndRequestNotificationPermission())
          },
          requestPushToken: () => {
            return withSafeLogging(async () => (await getPushToken()) as string)
          },
          startLineLogin: (channelId) => {
            return nativeLineLoginBridge.startLineLogin(channelId).then((result) => {
              if ('error' in result) {
                loggingNativeInformation(new Error(`[LINE:${result.error.code}] ${result.error.message}`))
              }
              return result
            })
          },
          setAppStartedAt: () => {
            return withSafeLogging(async () => await webBridge.setAppStartedAt())
          },
          setUseSafeArea: (useSafeArea) => {
            return withSafeLogging(async () => await webBridge.setUseSafeArea(useSafeArea))
          },
          setSafeAreaBackgroundColor: (safeAreaBackgroundColor) => {
            return withSafeLogging(async () => await webBridge.setSafeAreaBackgroundColor(safeAreaBackgroundColor))
          },
        }}
      >
        {children}
      </NativeBridgeStoreContext.Provider>
    )
  },
)

export { useBridgeEventListener, useBridgeLoose, useBridgeStatus, useBridgeStore }
